import React, { createContext, useContext, useState } from "react";

// Approximate exchange rate: 1 USD = 18.5 ZAR
export const ZAR_TO_USD_RATE = 18.5;

export type Currency = "ZAR" | "USD";

interface CurrencyContextValue {
  currency: Currency;
  toggleCurrency: () => void;
  formatPrice: (zarAmount: number) => string;
}

const CurrencyContext = createContext<CurrencyContextValue | undefined>(
  undefined,
);

export function CurrencyProvider({ children }: { children: React.ReactNode }) {
  const [currency, setCurrency] = useState<Currency>(() => {
    return (localStorage.getItem("displayCurrency") as Currency) || "ZAR";
  });

  const toggleCurrency = () => {
    setCurrency((prev) => {
      const next = prev === "ZAR" ? "USD" : "ZAR";
      localStorage.setItem("displayCurrency", next);
      return next;
    });
  };

  const formatPrice = (zarAmount: number): string => {
    if (currency === "USD") {
      const usdAmount = zarAmount / ZAR_TO_USD_RATE;
      return `$${usdAmount.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
    return `R${zarAmount.toLocaleString("en-ZA", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  return (
    <CurrencyContext.Provider value={{ currency, toggleCurrency, formatPrice }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency(): CurrencyContextValue {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    throw new Error("useCurrency must be used within a CurrencyProvider");
  }
  return ctx;
}
